/**
 * One mention list, shared by everything that shows it.
 *
 * WHY THIS IS NOT useState IN EACH COMPONENT, which is what it was: the bell
 * and the Mentions page each held their own copy of the same list. Marking
 * everything read on the page updated the page's copy; the bell kept its own
 * until its next two-minute poll came round, so the badge sat there showing a
 * number for something already dealt with. That is not a polling delay to be
 * tuned - it is two components disagreeing about one fact.
 *
 * THIS PROJECT HAS SHIPPED THIS EXACT BUG TWICE, both times in theme.ts (once
 * in web/, then again in admin/ because the file is a copy). The rule written
 * down after the second one: a hook wrapping global state must NOT use plain
 * useState internally - it reads as shared and behaves as per-instance, and
 * the failure is invisible until a SECOND consumer arrives, long after the
 * code looks proven. So: one value outside React, useSyncExternalStore to
 * subscribe, every consumer re-rendering together.
 *
 * MARKING IS OPTIMISTIC. The badge clears on the click, not on the round trip.
 * A notification count that lags its own dismissal teaches people the button
 * did not work, and they press it again. If the server refuses, the next
 * refresh puts the truth back - the cost of being briefly wrong about a number
 * nobody is acting on is far lower than the cost of feeling broken.
 */

import { useSyncExternalStore } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from './firebase';

export interface Mention {
  id: string;
  byEmail: string;
  scope: string;
  key: string;
  label: string;
  excerpt: string;
  atMs: number;
  seen: boolean;
}

interface State {
  mentions: Mention[];
  loaded: boolean;
  error: string | null;
}

let state: State = { mentions: [], loaded: false, error: null };
const listeners = new Set<() => void>();

function set(next: Partial<State>) {
  // A NEW OBJECT EVERY TIME. useSyncExternalStore compares by reference, so
  // mutating in place would notify listeners that then see no change and skip
  // the render - the same silent nothing this file exists to prevent.
  state = { ...state, ...next };
  for (const l of listeners) l();
}

const subscribe = (fn: () => void) => {
  listeners.add(fn);
  return () => { listeners.delete(fn); };
};

/** Everything that shows mentions reads from here. */
export function useMentions(): State & { unread: number } {
  const s = useSyncExternalStore(subscribe, () => state, () => state);
  return { ...s, unread: s.mentions.filter((m) => !m.seen).length };
}

let inFlight: Promise<void> | null = null;

/**
 * Reload from the server.
 *
 * Concurrent calls share one request: the bell's poll and the page opening at
 * the same moment is the normal case, not the exception, and two identical
 * reads racing produces two renders and occasionally the older answer last.
 */
export function refresh(orgId: string | null): Promise<void> {
  if (!orgId) return Promise.resolve();
  if (inFlight) return inFlight;

  inFlight = (async () => {
    try {
      const r = await httpsCallable(functions, 'listMentions')({ orgId });
      const list = (r.data as { mentions?: Mention[] }).mentions ?? [];
      set({ mentions: list, loaded: true, error: null });
    } catch (err) {
      // The list keeps whatever it had. A failed refresh must not blank a
      // page somebody is reading, and the bell simply shows the last count it
      // knew rather than dropping to zero, which would look like "all clear".
      set({ loaded: true, error: err instanceof Error ? err.message : 'Could not read mentions.' });
    } finally {
      inFlight = null;
    }
  })();

  return inFlight;
}

/**
 * Mark some read: locally first, then on the server.
 *
 * Returns once the server has answered, so a caller can await it, but every
 * subscriber has already re-rendered by then.
 */
export async function markRead(orgId: string | null, ids: readonly string[]): Promise<void> {
  const wanted = ids.filter((id) => state.mentions.some((m) => m.id === id && !m.seen));
  if (!orgId || !wanted.length) return;

  const before = state.mentions;
  set({ mentions: before.map((m) => (wanted.includes(m.id) ? { ...m, seen: true } : m)) });

  try {
    await httpsCallable(functions, 'markMentionsSeen')({ orgId, ids: wanted });
  } catch {
    // Put back exactly what was there. Refreshing instead would hide the
    // failure behind whatever the server happens to say, and the person would
    // never learn their click did not stick.
    set({ mentions: before, error: 'That did not save. The mention is still unread.' });
  }
}

/** Drop everything when the signed-in org changes. */
export function reset(): void {
  state = { mentions: [], loaded: false, error: null };
  for (const l of listeners) l();
}
